// Presentation of the status contract. Nothing here derives a phase (AD-8): the phase is shown as the API
// sent it; these functions only choose how to draw it, and every tone also carries a symbol and a word,
// so status never depends on colour alone.
import type { Condition, Environment, EnvironmentSummary, Phase, ArtefactIdentity, Artefact, AppArtefact, ImageChoice } from '../api/types';

export type Tone = 'ok' | 'progress' | 'idle' | 'bad' | 'unknown' | 'warn';

export interface Badge {
  tone: Tone;
  /** A text symbol shown next to the label (not colour-dependent). */
  symbol: string;
  label: string;
  /** Screen-reader / tooltip sentence. */
  title: string;
}

const SYMBOL: Record<Tone, string> = { ok: '✓', progress: '…', idle: '■', bad: '✕', unknown: '?', warn: '!' };

const PHASE_TONE: Record<Phase, Tone> = {
  Ready: 'ok',
  Pending: 'progress',
  DBInit: 'progress',
  AppInit: 'progress',
  Stopped: 'idle',
  Paused: 'idle',
  Failed: 'bad',
};

const PHASE_TEXT: Record<Phase, string> = {
  Ready: 'serving',
  Pending: 'waiting to start provisioning',
  DBInit: 'preparing the database',
  AppInit: 'installing the application',
  Stopped: 'stopped by request',
  Paused: 'reconciliation paused',
  Failed: 'the last reconcile ended in a terminal condition; see Conditions',
};

export function phaseBadge(phase: Phase | string): Badge {
  const tone = PHASE_TONE[phase as Phase] ?? 'unknown';
  return { tone, symbol: SYMBOL[tone], label: phase, title: `Phase ${phase}: ${PHASE_TEXT[phase as Phase] ?? 'unknown phase'}` };
}

// Conditions whose True is abnormal (kstatus polarity).
const ABNORMAL_TRUE = new Set(['Stalled', 'Reconciling']);

export function conditionBadge(c: Condition): Badge {
  let tone: Tone;
  if (c.status === 'Unknown') tone = 'unknown';
  else if (c.type === 'Stalled') tone = c.status === 'True' ? 'bad' : 'ok';
  else if (c.type === 'Reconciling') tone = c.status === 'True' ? 'progress' : 'ok';
  else tone = c.status === 'True' ? 'ok' : 'progress';
  const label = ABNORMAL_TRUE.has(c.type) ? (c.status === 'True' ? c.type : `not ${c.type}`) : c.status === 'True' ? 'yes' : c.status === 'False' ? 'not yet' : 'unknown';
  return { tone, symbol: SYMBOL[tone], label, title: `${c.type} is ${c.status} (${c.reason})` };
}

/** One line for the list view, from the summary fields (or the conditions, when the detail is loaded). */
export function conditionSummary(env: EnvironmentSummary & Partial<Pick<Environment, 'conditions' | 'generation' | 'observedGeneration'>>): Badge {
  if (env.tornDown) return mk('idle', 'torn down', 'A teardown has finished; the object is only the record.');
  if (env.deleting) return mk('progress', 'deleting', 'The ordered teardown runs; the environment disappears afterwards.');
  if (env.stalled?.status === 'True') return mk('bad', `Stalled: ${env.stalled.reason}`, env.stalled.message);
  const conds = env.conditions;
  const stalled = conds?.find((c) => c.type === 'Stalled' && c.status === 'True');
  if (stalled) return mk('bad', `Stalled: ${stalled.reason}`, stalled.message);
  const inFlight = env.actionInFlight;
  if (inFlight) return mk('progress', `${inFlight.type} in flight`, `Action ${inFlight.type} (${inFlight.id}) started ${inFlight.startedAt}.`);
  if (env.paused) return mk('idle', 'paused', 'spec.paused is set; the operator does not reconcile.');
  if (!conds) return mk('unknown', 'loading…', 'Conditions are not loaded yet.');
  const reconciling = conds.find((c) => c.type === 'Reconciling' && c.status === 'True');
  if (reconciling) return mk('progress', `Reconciling: ${reconciling.reason}`, reconciling.message);
  const positive = conds.filter((c) => !ABNORMAL_TRUE.has(c.type));
  const notTrue = positive.filter((c) => c.status !== 'True');
  if (env.generation !== undefined && env.observedGeneration !== undefined && env.observedGeneration < env.generation) {
    return mk('progress', 'edit not seen yet', `The operator has reconciled generation ${env.observedGeneration}; the spec is at ${env.generation}.`);
  }
  if (notTrue.length === 0) return mk('ok', `all ${positive.length} conditions true`, positive.map((c) => c.type).join(', '));
  const first = notTrue[0]!;
  return mk(
    first.status === 'Unknown' ? 'unknown' : 'progress',
    `${positive.length - notTrue.length}/${positive.length} true; ${first.type}: ${first.reason}`,
    notTrue.map((c) => `${c.type}=${c.status} (${c.reason}): ${c.message}`).join('\n'),
  );
}

function mk(tone: Tone, label: string, title: string): Badge {
  return { tone, symbol: SYMBOL[tone], label, title };
}

/** Conditions sorted so that what explains a stuck environment comes first. */
export function orderedConditions(conds: Condition[]): Condition[] {
  const rank = (c: Condition) => {
    if (c.type === 'Stalled' && c.status === 'True') return 0;
    if (c.type === 'Reconciling' && c.status === 'True') return 1;
    if (!ABNORMAL_TRUE.has(c.type) && c.status !== 'True') return 2;
    return 3;
  };
  return [...conds].sort((a, b) => rank(a) - rank(b));
}

/** `https://minio/.../<peaksolution|cashdesk|ticketservice|contingentservice>/<branch>/<pipeline>/<file>` → `<branch> #<pipeline>`; else the file name. */
export function buildLabel(a: Pick<ArtefactIdentity, 'canonicalUrl' | 'artefactId'> | Pick<AppArtefact, 'canonicalUrl'> | Pick<Artefact, 'url'> | undefined): string {
  if (!a) return '—';
  const raw = 'url' in a ? a.url : (a.canonicalUrl ?? ('artefactId' in a ? a.artefactId : undefined));
  if (!raw) return '—';
  const noQuery = raw.split('?')[0]!;
  const m = /\/(?:peaksolution|cashdesk|ticketservice|contingentservice)\/([^/]+)\/([^/]+)\/[^/]+$/.exec(noQuery);
  if (m) return `${m[1]} #${m[2]}`;
  return noQuery.split('/').pop() || noQuery;
}

/** The build an environment runs (status), else what it asked for (spec). */
export function environmentBuild(env: Partial<Pick<Environment, 'builds' | 'spec'>>): string {
  if (env.builds?.peaksolution?.base) return buildLabel(env.builds.peaksolution.base);
  if (env.spec?.peaksolution?.build?.base) return `${buildLabel(env.spec.peaksolution.build.base)} (requested)`;
  return '—';
}

export function shortSha(sha?: string): string {
  return sha ? sha.slice(0, 12) : '—';
}

/** Never shows a presign query (FR-15); the API should not send one, this is a belt. */
export function displayUrl(url?: string): string {
  return url ? url.split('?')[0]! : '—';
}

export function shopLinks(env: Partial<Pick<Environment, 'hosts' | 'url'>>): { label: string; href: string }[] {
  const out: { label: string; href: string }[] = [];
  const seen = new Set<string>();
  const add = (label: string, host: string) => {
    const href = /^https?:\/\//.test(host) ? host : `https://${host}/`;
    if (!seen.has(href)) { seen.add(href); out.push({ label, href }); }
  };
  if (env.url) add('shop', env.url);
  env.hosts?.peaksolution?.forEach((h) => add('shop', h));
  env.hosts?.cashdesk?.forEach((h) => add('cashdesk', h));
  env.hosts?.ticketservice?.forEach((h) => add('ticketservice', h));
  env.hosts?.contingentservice?.forEach((h) => add('contingentservice', h));
  return out;
}

export function formatTime(ts?: string): string {
  if (!ts) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toISOString().replace('T', ' ').replace(/\.\d+Z$/, 'Z');
}

/**
 * How a deployed application artefact's bytes were verified (FR-11 amended 2026-10-05), as the API says it; nothing
 * is inferred here. `first-fetch`: no sha256 was declared, the digest was computed on the URL's first fetch.
 */
/** Where a mysql/php image came from (status.images), in one line. */
export function imageBasis(c: ImageChoice | undefined): string {
  if (!c) return 'not decided yet';
  const basis = c.basis ?? '';
  return c.source === 'catalog' ? `from the operator's image catalog${basis ? `: ${basis}` : ''}` : basis || (c.source ?? '');
}

export function verificationText(a: { verification?: AppArtefact['verification'] }): string {
  return a.verification === 'first-fetch' ? 'verified on first fetch' : 'declared sha256 verified';
}

/** The data's classification (D2 of docs/proposal-environment-access.md) as a short badge. */
export function classificationBadge(c: Environment['dataClassification'] | undefined): Badge | undefined {
  if (!c) return undefined;
  if (c === 'production-raw') return mk('bad', 'raw', 'Raw production data: members also need the entitlement to it.');
  return mk('ok', c, `${c} data: the access list is enough.`);
}

/** The caller's role on an environment, drawn so owner, operator, viewer and admin differ in more than hue. */
export function roleTone(role: string | undefined): string {
  return role === 'owner' ? 'tone-dark' : role === 'operator' ? 'tone-progress' : role === 'admin' ? 'tone-outline' : 'tone-idle';
}

const CONDITION_LABEL: Record<string, string> = {
  ChartApplied: 'Chart applied',
  DatabaseReady: 'Database ready',
  HtaccessValid: 'htaccess valid',
  DataPrepared: 'Data prepared',
  AssertionsPassed: 'Assertions passed',
  ActionAccepted: 'Last action accepted',
  ApplicationReady: 'Application set up',
  WorkloadsReady: 'Workloads ready',
  CertificatesReady: 'Certificates issued',
};

export interface Tile {
  type: string;
  title: string;
  /** reason · since */
  sub: string;
  message: string;
  state: 'ok' | 'warn' | 'pending' | 'bad' | 'unknown';
}

/** One tile per positive condition (Stalled and Reconciling are told by the banner and the header); the one that stalls it is bad. */
export function healthTiles(conds: Condition[]): Tile[] {
  const stalled = conds.find((c) => c.type === 'Stalled' && c.status === 'True');
  return orderedConditions(conds)
    .filter((c) => !ABNORMAL_TRUE.has(c.type))
    .map((c) => {
      const state: Tile['state'] =
        c.status === 'Unknown' ? 'unknown'
          : c.status !== 'True' ? (stalled && stalled.reason === c.reason ? 'bad' : 'pending')
            : c.reason === 'PassedWithAcceptedViolations' ? 'warn' : 'ok';
      return { type: c.type, title: CONDITION_LABEL[c.type] ?? c.type, sub: `${c.reason} · ${relativeTime(c.lastTransitionTime)}`, message: c.message, state };
    });
}

/** The gate passed only because reviewed violations were accepted (spec.db.acceptedViolations): its message, else undefined. */
export function acceptedViolations(conds: Condition[] | undefined): { count?: number; message: string } | undefined {
  const c = conds?.find((x) => x.type === 'AssertionsPassed' && x.status === 'True' && x.reason === 'PassedWithAcceptedViolations');
  if (!c) return undefined;
  const m = /with (\d+) violation/.exec(c.message);
  return { count: m ? Number(m[1]) : undefined, message: c.message };
}

type DataChecks = NonNullable<Environment['dataChecks']>;
type GateResult = NonNullable<DataChecks['gate']>;
type AcceptedEntry = DataChecks['acceptedViolations'][number];

/** The last gate run, as a badge: passed, passed with accepted violations, or blocked. */
export function gateBadge(g: GateResult): Badge {
  if (g.outcome === 'Blocked') {
    return { tone: 'bad', symbol: SYMBOL.bad, label: 'Blocked', title: 'The data checks blocked this dataset: the environment does not start until each violation is fixed or accepted' };
  }
  if (g.outcome === 'PassedWithAcceptedViolations') {
    return { tone: 'warn', symbol: SYMBOL.warn, label: 'Passed with accepted violations', title: 'The data checks passed because reviewed violations were accepted' };
  }
  return { tone: 'ok', symbol: SYMBOL.ok, label: 'Passed', title: 'Every data check passed' };
}

/** "26 checks: 24 passed, 1 violated, 1 accepted, 0 broken, 0 not applicable" (zero accepted/skipped left out). */
export function gateCounts(g: GateResult): string {
  const c = g.counts;
  const parts = [`${c.passed} passed`, `${c.violated} violated`];
  if (c.accepted) parts.push(`${c.accepted} accepted`);
  parts.push(`${c.broken} broken`, `${c.notApplicable} not applicable`);
  if (c.skipped) parts.push(`${c.skipped} skipped`);
  return `${c.checks} checks: ${parts.join(', ')}`;
}

/** The day acceptances began to name their dataset; older entries are unbound. */
export const BINDING_SINCE = '2026-10-20';

/** Which dataset an acceptance belongs to, in words, with a tone for its pill. */
export function bindingText(a: AcceptedEntry, datasetGeneration: number): { label: string; title: string; tone: string } {
  if (a.binding === 'unbound') {
    return { label: `unbound (made before ${BINDING_SINCE})`, tone: 'tone-warn',
      title: 'Made before acceptances named their dataset. It applies until the next refresh-data removes it.' };
  }
  if (a.binding === 'stale') {
    return { label: `stale: made for dataset ${a.datasetGeneration ?? '?'}`, tone: 'tone-idle',
      title: `Made for another dataset than the one on disk (${datasetGeneration}); the operator ignores it. Remove it.` };
  }
  return { label: `dataset ${a.datasetGeneration ?? datasetGeneration}${a.backupId ? ` · ${a.backupId}` : ''}`, tone: 'tone-ok',
    title: 'Made for the dataset on disk; the next refresh-data removes it.' };
}

/** What the last gate made of an acceptance. */
export function lastGateText(a: AcceptedEntry): { label: string; title: string; tone: string } {
  if (a.lastGate === 'used') return { label: 'used', tone: 'tone-warn', title: 'The last gate found this violation and let it pass because of this acceptance.' };
  if (a.lastGate === 'unused') return { label: 'unused', tone: 'tone-idle', title: 'The last gate ran with it, and no check reported this violation. Remove it.' };
  return { label: 'not run yet', tone: 'tone-idle', title: 'The last gate ran without it: it counts from the next gate run (a blocked gate re-runs on its own).' };
}

/**
 * The overview's banner about the data checks: the gate blocked (with how many violations wait for a decision), or
 * passed only because violations were accepted. From dataChecks when the API sends it, else from the condition.
 */
export function dataChecksBanner(e: Environment | undefined): { tone: 'bad' | 'warn'; title: string; text: string } | undefined {
  const g = e?.dataChecks?.gate;
  if (g?.outcome === 'Blocked') {
    const n = g.violations.length + (g.moreViolations ?? 0);
    const broken = g.broken.length + (g.moreBroken ?? 0);
    return {
      tone: 'bad',
      title: `The data checks blocked this dataset: ${n} violation${n === 1 ? '' : 's'}${broken ? ` and ${broken} broken check${broken === 1 ? '' : 's'}` : ''}.`,
      text: 'Fix the preparation and refresh the data, or, after review, accept a violation for this environment.',
    };
  }
  const accepted = e?.dataChecks?.acceptedViolations ?? [];
  if (g?.outcome === 'PassedWithAcceptedViolations') {
    const used = accepted.filter((a) => a.lastGate === 'used').length || g.accepted.length + (g.moreAccepted ?? 0);
    return { tone: 'warn', title: `Checks passed with ${used} accepted violation${used === 1 ? '' : 's'}.`,
      text: 'What was accepted stays live in this environment. Review who accepted what, and why.' };
  }
  const fromCondition = acceptedViolations(e?.conditions);
  if (fromCondition && !g) {
    return { tone: 'warn', title: `Checks passed with ${fromCondition.count ?? 'some'} accepted violation${fromCondition.count === 1 ? '' : 's'}.`, text: fromCondition.message };
  }
  return undefined;
}

/** "5 min ago", "3 days ago"; the exact time goes in a title. */
export function relativeTime(ts?: string, now = Date.now()): string {
  if (!ts) return '—';
  const t = new Date(ts).getTime();
  if (Number.isNaN(t)) return ts;
  const s = Math.round((now - t) / 1000);
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}
